import React from 'react';
import LoginShell from '../components/login/LoginShell';
import { ResetPasswordStep } from '../components/login/ResetPasswordStep';
import {
  ActionDoneStep,
  ActionLinkInvalidStep,
  ActionLinkRetryStep,
  ActionLinkWorking,
} from '../components/login/AuthActionSteps';
import { AuthActionStatus, VERIFY_EMAIL_MODE, useAuthAction } from '../hooks/useAuthAction';

/**
 * `/auth/action` — where the password-reset and sign-up verification emails'
 * links land. Public, and framed exactly like `/login` so the person never
 * feels they left it.
 */
type Copy = { title: string; subtitle: string };

const COPY: Record<AuthActionStatus, Copy> = {
  checking: { title: 'Checking your link', subtitle: 'One moment.' },
  unreachable: { title: "We couldn't check your link", subtitle: 'Check your connection, then try again.' },
  form: { title: 'Set a new password', subtitle: '' },
  'signing-in': { title: 'Email confirmed', subtitle: 'Signing you in…' },
  done: { title: 'Password updated', subtitle: 'Sign in with your new password.' },
  invalid: {
    title: 'This link has expired',
    subtitle: 'Links work once. Request a new one from the sign-in page.',
  },
};

/** Where a verification link says something different from a reset link. */
const VERIFY_EMAIL_COPY: Partial<Record<AuthActionStatus, Copy>> = {
  checking: { title: 'Confirming your email…', subtitle: 'One moment.' },
  done: { title: 'Email confirmed', subtitle: 'Sign in to get started.' },
};

const AuthAction: React.FC = () => {
  const action = useAuthAction();
  const { status } = action;
  const copy = (action.mode === VERIFY_EMAIL_MODE && VERIFY_EMAIL_COPY[status]) || COPY[status];
  const subtitle = status === 'form' ? `For ${action.email}` : copy.subtitle;

  return (
    <LoginShell title={copy.title} subtitle={subtitle} error={action.error}>
      {status === 'checking' && <ActionLinkWorking label={copy.title} />}
      {status === 'signing-in' && <ActionLinkWorking label={copy.subtitle} />}
      {status === 'unreachable' && <ActionLinkRetryStep onRetry={action.retryCheck} />}
      {status === 'form' && (
        <ResetPasswordStep
          email={action.email}
          password={action.password}
          setPassword={action.setPassword}
          confirmPassword={action.confirmPassword}
          setConfirmPassword={action.setConfirmPassword}
          rules={action.rules}
          canSubmit={action.canSubmit}
          saving={action.saving}
          onSubmit={action.handleSubmit}
        />
      )}
      {status === 'done' && <ActionDoneStep onSignIn={action.goToSignIn} />}
      {status === 'invalid' && <ActionLinkInvalidStep onBack={action.backToSignIn} />}
    </LoginShell>
  );
};

export default AuthAction;
